import { escapeAttrValue, FilterXSS } from 'xss';

/**
 * The HTML rendered through `sanitizeHtml` comes from trusted Box Office
 * rich-text fields, so the filter is lenient: it strips active content
 * (script tags, inline event handlers, javascript: URLs) and passes all
 * other markup and attributes through unchanged to preserve formatting.
 */
const htmlFilter = new FilterXSS({
  stripIgnoreTagBody: ['script'],
  // Drop unknown tags themselves but keep their children.
  onIgnoreTag: () => '',
  // Keep attributes outside the default allowlist (style, class, ...)
  // except event handlers.
  onIgnoreTagAttr: (_tag, name, value) => {
    if (/^on/i.test(name)) {
      return undefined;
    }
    return `${name}="${escapeAttrValue(value)}"`;
  },
});

/**
 * Sanitizes an HTML string for use with `dangerouslySetInnerHTML`.
 * Strips scripts, event handlers, and other active content while keeping
 * rich-text markup. Use this for any HTML that originates from the
 * API (e.g. Box Office rich-text fields) before rendering it.
 */
export const sanitizeHtml = (html?: string | null): string => {
  if (!html) {
    return '';
  }
  return htmlFilter.process(html);
};

export function CapitalizeFirstLetter(value: string) {
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

export function GetCountryNameFromCode(code: string, locale: string) {
  return new Intl.DisplayNames([locale], { type: 'region' }).of(code);
}

export const getNameInitials = (fullName: string) => {
  const names = fullName.split(' ');
  return (
    (names[0]?.substring(0, 1).toUpperCase() ?? '') +
    (names[names.length - 1]?.substring(0, 1).toUpperCase() ?? '')
  );
};

const htmlEntities: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
};

export const stripHtmlTags = (html?: string | null): string => {
  if (!html) {
    return '';
  }
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z0-9#]+;/gi, (entity) => htmlEntities[entity] ?? entity)
    .replace(/\s+/g, ' ')
    .trim();
};
